/**
 * requestAppointmentSlots: 候補日時（空き枠）を取得する。
 * US-D2-03、FR-Y1, FR-C7, FR-CAL1。
 * BR-U6-4: 複数CVの空き枠取得は memberGroupId を集約・重複排除してから呼び出す。
 */

import { relayClient } from "../core/relayClient";
import { classifyError, logClientWarning } from "../core/errorHandler";
import { APPOINTMENT_RANGE_DAYS } from "../core/appointmentConstants";
import { nowJstDateTime, buildRequestDateTime } from "../core-ui/datetime";
import type { AppointmentSlot, ContentDisplayResult } from "../types";

function buildRange(): { rangeStart: string; rangeEnd: string } {
  // range も JST ローカル文字列（`yyyy-MM-ddTHH:mm:ss`、オフセットなし）で送信する（`toISOString()`〔UTC化〕を廃止、Q4=A）。
  // 実行環境TZ=Asia/Tokyo 前提で端末ローカルの壁時計を用いる（datetime-contract.md §2）。
  const rangeStart = nowJstDateTime();
  const end = new Date(Date.now() + APPOINTMENT_RANGE_DAYS * 24 * 60 * 60 * 1000);
  const rangeEnd = buildRequestDateTime(
    end.getFullYear(),
    end.getMonth() + 1,
    end.getDate(),
    end.getHours(),
    end.getMinutes(),
    end.getSeconds()
  );
  return { rangeStart, rangeEnd };
}

/** 空き枠取得結果。エラー時はhasError=trueで区別する。 */
export interface SlotFetchResult {
  slots: AppointmentSlot[];
  hasError: boolean;
}

export async function requestAppointmentSlotsForSingle(memberGroupId: string): Promise<SlotFetchResult> {
  const { rangeStart, rangeEnd } = buildRange();
  const response = await relayClient.getAvailableSlots<AppointmentSlot[]>({
    memberGroupId,
    rangeStart,
    rangeEnd,
  });

  if (!response.ok || !response.data) {
    const classified = classifyError(response);
    logClientWarning(`getAvailableSlots failed: ${classified.kind}`, memberGroupId);
    return { slots: [], hasError: true };
  }

  // APIはAppointmentSlot配列を直接返す
  return { slots: response.data, hasError: false };
}

/**
 * 複数CV（メインCV・レコメンドCV）の空き枠取得を、memberGroupIdの重複排除の上で並行実行する（BR-U6-4）。
 */
export interface AllCvsSlotFetchResult {
  slotsByMemberGroup: Map<string, AppointmentSlot[]>;
  hasError: boolean;
}

export async function requestAppointmentSlotsForAllCvs(
  results: ContentDisplayResult[]
): Promise<AllCvsSlotFetchResult> {
  const targetItems = results.filter((r) => r.displayMode !== "HIDDEN" && r.memberGroupId);
  const uniqueMemberGroupIds = Array.from(
    new Set(targetItems.map((r) => r.memberGroupId).filter((id): id is string => id !== null))
  );

  const slotsByMemberGroup = new Map<string, AppointmentSlot[]>();
  let hasError = false;
  await Promise.all(
    uniqueMemberGroupIds.map(async (memberGroupId) => {
      const result = await requestAppointmentSlotsForSingle(memberGroupId);
      if (result.hasError) hasError = true;
      slotsByMemberGroup.set(memberGroupId, result.slots);
    })
  );

  return { slotsByMemberGroup, hasError };
}
